/**
 * Custom SVG icons for Aspexit services
 * Simple, modern, and brand-aligned designs
 */

interface IconProps {
  className?: string;
  color?: string;
}

export function DataScienceIcon({ className = "w-24 h-24", color = "#55BC8C" }: IconProps) {
  return (
    <svg viewBox="0 0 100 100" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Bar chart with analytics theme */}
      <rect x="15" y="50" width="12" height="35" fill={color} rx="2" opacity="0.8" />
      <rect x="32" y="35" width="12" height="50" fill={color} rx="2" />
      <rect x="49" y="45" width="12" height="40" fill={color} rx="2" opacity="0.9" />
      <rect x="66" y="25" width="12" height="60" fill={color} rx="2" />
      {/* Trend line */}
      <polyline
        points="21,56 38,41 55,51 72,31"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.6"
      />
      {/* Circle nodes on trend line */}
      <circle cx="21" cy="56" r="3" fill={color} />
      <circle cx="38" cy="41" r="3" fill={color} />
      <circle cx="55" cy="51" r="3" fill={color} />
      <circle cx="72" cy="31" r="3" fill={color} />
    </svg>
  );
}

export function DossiersTechniquesIcon({ className = "w-24 h-24", color = "#9B59B6" }: IconProps) {
  return (
    <svg viewBox="0 0 100 100" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Document/report icon */}
      <rect x="25" y="15" width="50" height="70" fill={color} rx="3" opacity="0.15" />
      <rect x="30" y="20" width="40" height="60" fill="white" stroke={color} strokeWidth="2" rx="2" />
      {/* Lines representing text */}
      <line x1="38" y1="30" x2="62" y2="30" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.6" />
      <line x1="38" y1="40" x2="62" y2="40" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.6" />
      <line x1="38" y1="50" x2="58" y2="50" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.6" />
      <line x1="38" y1="60" x2="62" y2="60" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.6" />
      <line x1="38" y1="70" x2="54" y2="70" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.6" />
    </svg>
  );
}

export function InfographiesIcon({ className = "w-24 h-24", color = "#E96656" }: IconProps) {
  return (
    <svg viewBox="0 0 100 100" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Image/visual icon with palette theme */}
      <rect x="20" y="25" width="60" height="50" fill="white" stroke={color} strokeWidth="2.5" rx="3" />
      {/* Mountain/landscape inside */}
      <polygon points="30,60 45,45 55,52 70,38 70,65 30,65" fill={color} opacity="0.3" />
      <polygon points="35,65 50,50 60,58 70,48 70,65" fill={color} opacity="0.5" />
      {/* Sun/circle */}
      <circle cx="60" cy="38" r="6" fill={color} opacity="0.4" />
      {/* Frame corner details */}
      <circle cx="26" cy="31" r="2" fill={color} />
      <circle cx="74" cy="31" r="2" fill={color} />
      <circle cx="26" cy="69" r="2" fill={color} />
      <circle cx="74" cy="69" r="2" fill={color} />
    </svg>
  );
}

export function FormationsIcon({ className = "w-24 h-24", color = "#0D974E" }: IconProps) {
  return (
    <svg viewBox="0 0 100 100" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Graduation cap / book icon */}
      {/* Book base */}
      <rect x="30" y="45" width="40" height="35" fill={color} rx="2" opacity="0.2" />
      <rect x="33" y="48" width="34" height="29" fill="white" stroke={color} strokeWidth="2" rx="1" />
      {/* Book pages */}
      <line x1="50" y1="48" x2="50" y2="77" stroke={color} strokeWidth="1.5" opacity="0.4" />
      {/* Graduation cap on top */}
      <polygon points="50,25 65,32 50,38 35,32" fill={color} />
      <rect x="49" y="32" width="2" height="13" fill={color} opacity="0.8" />
      <circle cx="50" cy="46" r="2" fill={color} />
    </svg>
  );
}

export function AuditExpertiseIcon({ className = "w-24 h-24", color = "#F9A826" }: IconProps) {
  return (
    <svg viewBox="0 0 100 100" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Magnifying glass with checklist */}
      {/* Checklist background */}
      <rect x="25" y="20" width="40" height="50" fill={color} rx="2" opacity="0.15" />
      {/* Checkmarks */}
      <polyline points="32,32 35,36 40,28" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="32,45 35,49 40,41" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="32,58 35,62 40,54" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Lines next to checkmarks */}
      <line x1="45" y1="32" x2="58" y2="32" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <line x1="45" y1="45" x2="58" y2="45" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <line x1="45" y1="58" x2="58" y2="58" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      {/* Magnifying glass */}
      <circle cx="62" cy="60" r="12" fill="none" stroke={color} strokeWidth="2.5" />
      <line x1="71" y1="69" x2="80" y2="78" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export function InterventionsIcon({ className = "w-24 h-24", color = "#8B6F47" }: IconProps) {
  return (
    <svg viewBox="0 0 100 100" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Microphone with sound waves */}
      {/* Microphone body */}
      <rect x="43" y="30" width="14" height="25" fill={color} rx="7" />
      <path d="M 38 50 Q 38 62 50 62 Q 62 62 62 50" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="50" y1="62" x2="50" y2="72" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="42" y1="72" x2="58" y2="72" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      {/* Sound waves */}
      <path d="M 68 35 Q 73 40 73 50 Q 73 60 68 65" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <path d="M 32 35 Q 27 40 27 50 Q 27 60 32 65" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <path d="M 75 30 Q 82 38 82 50 Q 82 62 75 70" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.3" />
      <path d="M 25 30 Q 18 38 18 50 Q 18 62 25 70" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" opacity="0.3" />
    </svg>
  );
}
